(ns shared.core.components.theme
  (:require [shared.core.reactive :as re]
            [shared.core.components.interactive :as interactive]
            [shared.core.style :as style]
            [shared.core.components.tooltip :as tooltip]
            [shared.core.util :as util]))

(defn cur-theme [db]
  (let [theme (:theme (:local-storage db))]
    (if (:system theme)
      (if (util/dark-mode-system-setting?)
        (:dark theme)
        (:light theme))
      theme)))

(re/reg-sub :core/theme- cur-theme)

(re/reg-sub
  :core/theme-dark?-
  (fn [db]
    (= (cur-theme db) :dark)))

(re/reg-event-fx
  :core/set-theme re/middleware
  (fn [{:keys [local-storage]} [new-theme]]
    (re/dispatch [:core/set-app-data-theme])
    {:local-storage (assoc local-storage :theme new-theme)}))

(re/reg-event-db
  :core/set-app-data-theme
  (fn [db]
    (let [current-theme (cur-theme db)]
      (when-let [root-el (.getElementById js/document "app")]
        (if current-theme
          (.setAttribute root-el "data-theme" (name current-theme))
          (.removeAttribute root-el "data-theme")))
      db)))

(defn reduce-props
  "Reduces each prop to a single value"
  [base-args theme-props]
  (some->> theme-props
           (keys)
           (map (fn [k]
                  [k (merge (get base-args k) (get theme-props k))]))
           (into {})))

(defn merge-theme-props
  "Merge in props to general prop value based on `theme-props` that are for current theme"
  [args theme-props]
  (-> args
      (dissoc :theme)
      (merge (reduce-props args theme-props))))

(defn dark-theme-toggle [_]
  (let [dark?- (re/subscribe [:core/theme-dark?-])
        hover?* (re/ratom false)]
    (fn [{:keys [style]}]
      (let [dark? @dark?-
            bulb-on-color "#e8e52d"
            ring-color (if dark? (style/dark-color :on-background :base) (style/color :gray :base))
            bulb-size 16]
        [tooltip/tooltip
         {:tooltip-content
          (str (if dark? "Disable" "Enable") " Dark Mode")

          :target-content
          [interactive/interactive
           :div {:style (merge {:cursor :pointer
                                :padding 3
                                :position :relative
                                :height (* bulb-size 1.345)
                                :width bulb-size}
                               style)
                 :hover?* hover?*
                 :on-click #(re/dispatch [:core/set-theme (if dark? nil :dark)])}
           [:div {:style (cond-> {:background-color (if dark? (style/color :gray :base) bulb-on-color)
                                  :border (str "1px solid " ring-color)
                                  :border-radius "50%"
                                  :position :absolute
                                  :top 0
                                  :left 0
                                  :transition "0.2s"
                                  :height bulb-size
                                  :width bulb-size
                                  :z-index (inc (style/z :low))}

                                 @hover?*
                                 (assoc :background-color (if dark?
                                                            (util/hex->rgba bulb-on-color 0.6)
                                                            (util/hex->rgba (style/color :gray :base) 0.1))))}]
           (when @hover?*
             [:div {:style {:background (str "radial-gradient("
                                             (if dark?
                                               (util/hex->rgba bulb-on-color 0.6)
                                               (util/hex->rgba (style/color :gray :base) 0.1))
                                             ", transparent)")
                            :border-radius "50%"
                            :position :absolute
                            :top (* -0.15 bulb-size)
                            :left (* -0.15 bulb-size)
                            :transition "0.2s"
                            :height (* bulb-size 1.3)
                            :width (* bulb-size 1.3)}}])
           [:div {:style {:background-color ring-color
                          :border-radius (str "0px 0px " (/ bulb-size 16) "px " (/ bulb-size 16) "px")
                          :height (/ bulb-size 5)
                          :position :absolute
                          :top (* 0.87 bulb-size)
                          :left "50%"
                          :transform "translateX(-50%)"
                          :width (* bulb-size 0.6)
                          :z-index (style/z :low)}}]
           [:div {:style {:background-color ring-color
                          :border-radius (str "0px 0px " (/ bulb-size 6) "px " (/ bulb-size 6) "px")
                          :height (/ bulb-size 8)
                          :position :absolute
                          :top (* 1.22 bulb-size)
                          :left "50%"
                          :transform "translateX(-50%)"
                          :width (* bulb-size 0.5)
                          :z-index (style/z :low)}}]]}]))))

(defn theme
  "Component for conditionally adding theme styling based on active theme set for app. You can send the typical `style`
   property that are your base styles, but then you can also pass a property called `theme`. This is a map of theme
   properties, keyed by theme. The associated properties for the active theme are then merged in. Please note that the
   `cmp` arg can be a reagent component or a hiccup keyword, like `:div`.

   Ex:
   [c/theme c/a {:style {:color \"orange\"}
                 :theme {:dark-mode {:style {:color \"black\"}}}}
    \"Some Link\"]"
  [_ _ _]
  (let [theme- (re/subscribe [:core/theme-])]
    (fn [cmp & contents]
      (into [cmp]
            (mapv #(if (map? %)
                     (merge-theme-props % (when @theme- (get-in % [:theme @theme-])))
                     %)
                  contents)))))
